import { IDicom, IViewLabel, IQualityLabel } from 'models/icardio';

export const checkDicomForLabeledViewLabel = ({
  dicom,
  viewLabels,
  qualityLabels,
}: {
  dicom: IDicom;
  viewLabels?: IViewLabel[];
  qualityLabels?: IQualityLabel[];
}): { viewLabelIsLabeled: boolean; viewLabelIsRequired: boolean } => {
  const viewLabel = viewLabels?.find(label => label.dicom === dicom.uuid);
  const qualityLabel = qualityLabels?.find(label => label.dicom === dicom.uuid);

  const viewLabelIsLabeled = !!(viewLabel?.viewClass && qualityLabel?.qualityClass);
  const viewLabelIsRequired = true;

  return {
    viewLabelIsLabeled,
    viewLabelIsRequired,
  };
};

export const checkDicomForLabeledPhases = ({ dicom, viewLabels, phaseClasses, phaseLabels }) => {
  // get the required phases based on the view-label
  const viewLabel = viewLabels?.find(viewLabel => viewLabel?.dicom === dicom?.uuid);
  const requiredPhases = phaseClasses
    ?.filter(phaseClass => phaseClass?.viewClasses?.includes(viewLabel?.viewClass))
    ?.map(phaseClass => phaseClass?.value);

  // check if phases are required
  const phaseLabelsAreRequired = !!requiredPhases?.length;

  // if there are no required phases, return
  if (!phaseLabelsAreRequired)
    return {
      phaseLabelsAreLabeled: false,
      phaseLabelsAreRequired: false,
    };

  // get the phase labels for the given dicom
  const phaseLabelsForDicom = phaseLabels
    ?.filter(phaseLabel => phaseLabel?.dicom === dicom?.uuid)
    ?.map(phaseLabel => phaseLabel?.phaseClass);

  // check that all phases have been labeled, return false if any are missing
  for (let i = 0; i < requiredPhases?.length; i++) {
    const requiredPhase = requiredPhases[i];
    if (!phaseLabelsForDicom?.includes(requiredPhase))
      return {
        phaseLabelsAreLabeled: false,
        phaseLabelsAreRequired: true,
      };
  }

  // if all required phases are labeled, return true
  return {
    phaseLabelsAreLabeled: true,
    phaseLabelsAreRequired: true,
  };
};

export const checkDicomForLabeledKeypoints = ({
  dicom,
  viewLabels,
  phaseClasses,
  phaseLabels,
  keypointCollectionClasses,
  keypointCollectionLabels,
}) => {
  const viewLabel = viewLabels?.find(viewLabel => viewLabel?.dicom === dicom?.uuid);
  const viewClassUuid = viewLabel?.viewClass;

  const requiredPhases = phaseClasses
    ?.filter(phaseClass => phaseClass?.viewClasses?.includes(viewClassUuid))
    ?.map(phaseClass => phaseClass?.value);

  // optional collections (isRequired === false) are offered in the keypointer but never block completion
  const requiredKeypointCollectionClasses = keypointCollectionClasses?.filter(
    keypointCollectionClass =>
      keypointCollectionClass?.viewClasses?.includes(viewClassUuid) &&
      keypointCollectionClass?.isRequired !== false
  );
  const keypointCollectionsAreRequired = !!requiredKeypointCollectionClasses?.length;

  // if there are no required keypoint collections, return
  if (!keypointCollectionsAreRequired)
    return {
      keypointCollectionLabelsAreLabeled: false,
      keypointCollectionLabelsAreRequired: false,
    };

  // check if phases are required
  const phaseLabelsAreRequiredForDicom = !!requiredPhases?.length;

  // for each keypoint collection class, check if it is labeled
  for (let i = 0; i < requiredKeypointCollectionClasses?.length; i++) {
    const keypointCollectionClass = requiredKeypointCollectionClasses[i];

    const requiredPhasesForKeypointCollection = keypointCollectionClass?.phases; // TODO: update to phaseClasses
    const phasesAreRequiredForKeypointCollection = !!requiredPhasesForKeypointCollection?.length;

    // case a) if phases are required, check if keypoint collection labels exist for each phase
    if (phaseLabelsAreRequiredForDicom && phasesAreRequiredForKeypointCollection) {
      for (let j = 0; j < requiredPhasesForKeypointCollection?.length; j++) {
        // first, get the phase label for the required phase
        const requiredPhaseValue = requiredPhasesForKeypointCollection[j]?.value;
        const phaseLabel = phaseLabels?.find(
          phaseLabel =>
            phaseLabel?.dicom === dicom?.uuid && phaseLabel?.phaseClass === requiredPhaseValue
        );

        // next, look for the keypoint collection label for the required phase
        const keypointCollectionLabel = keypointCollectionLabels?.find(
          keypointCollectionLabel =>
            keypointCollectionLabel?.frame === phaseLabel?.frame &&
            keypointCollectionLabel?.keypointCollectionClass === keypointCollectionClass?.uuid
        );

        // if the keypoint collection label does not exist, return false
        if (!keypointCollectionLabel)
          return {
            keypointCollectionLabelsAreLabeled: false,
            keypointCollectionLabelsAreRequired: true,
          };

        // if the keypoint collection is not fully visible, skip it
        if (keypointCollectionLabel?.isFullyVisible === false) continue;

        // if not all keypoints are labeled, return false
        const requiredKeypoints = keypointCollectionClass?.keypointClasses;
        const keypointLabels = keypointCollectionLabel?.keypointLabels;
        const keypointLabelsAreLabeled =
          keypointLabels?.length > 0 &&
          keypointLabels?.length === requiredKeypoints?.length &&
          keypointLabels?.every(keypoint => keypoint?.x && keypoint?.y);
        if (!keypointLabelsAreLabeled)
          return {
            keypointCollectionLabelsAreLabeled: false,
            keypointCollectionLabelsAreRequired: true,
          };
      }
    }

    // case b) if phases are not required, check if keypoint collection labels exist for the dicom
    else {
      // check each keypoint collection label on the dicom for at least one completed label
      const dicomKeypointCollectionLabels = keypointCollectionLabels?.filter(
        keypointCollectionLabel =>
          keypointCollectionLabel?.dicom === dicom?.uuid &&
          keypointCollectionLabel?.keypointCollectionClass === keypointCollectionClass?.uuid
      );

      // if there are no keypoint collection labels, return false
      if (!dicomKeypointCollectionLabels?.length)
        return {
          keypointCollectionLabelsAreLabeled: false,
          keypointCollectionLabelsAreRequired: true,
        };

      let atLeastOneLabeled = false;

      for (const keypointCollectionLabel of dicomKeypointCollectionLabels) {
        // if the keypoint collection is not fully visible, break with atLeastOneLabeled = true
        if (keypointCollectionLabel?.isFullyVisible === false) {
          atLeastOneLabeled = true;
          break;
        }

        // if all keypoints are labeled, break with atLeastOneLabeled = true
        const requiredKeypoints = keypointCollectionClass?.keypointClasses;
        const keypointLabels = keypointCollectionLabel?.keypointLabels;
        const keypointLabelsAreLabeled =
          keypointLabels?.length > 0 &&
          keypointLabels?.length === requiredKeypoints?.length &&
          keypointLabels?.every(keypoint => keypoint?.x && keypoint?.y);
        if (keypointLabelsAreLabeled) {
          atLeastOneLabeled = true;
          break;
        }
      }

      if (!atLeastOneLabeled)
        return {
          keypointCollectionLabelsAreLabeled: false,
          keypointCollectionLabelsAreRequired: true,
        };
    }
  }

  return {
    keypointCollectionLabelsAreLabeled: true,
    keypointCollectionLabelsAreRequired: true,
  };
};

export const checkDicomForLabeledSegmentations = ({
  dicom,
  viewLabels,
  phaseLabels,
  segmentationClasses,
  segmentationLabels,
}) => {
  const viewLabel = viewLabels?.find(viewLabel => viewLabel?.dicom === dicom?.uuid);
  const viewClassUuid = viewLabel?.viewClass;

  // TODO: dynamically set this on the class table, see keypoint collection classes
  let requiredPhases: any = null;
  if (dicom?.mediaType?.includes('Video')) requiredPhases = ['Systolic', 'Diastolic'];

  const requiredSegmentationClasses = segmentationClasses?.filter(segmentationClass =>
    segmentationClass?.viewClasses?.includes(viewClassUuid)
  );
  const segmentationsAreRequired = !!requiredSegmentationClasses?.length;

  // if there are no required segmentation classes, return
  if (!segmentationsAreRequired)
    return {
      segmentationsAreLabeled: false,
      segmentationsAreRequired: false,
    };

  // check if phases are required
  const phaseLabelsAreRequiredForDicom = !!requiredPhases?.length;

  // for each segmentation class, check if it is labeled
  for (let i = 0; i < requiredSegmentationClasses?.length; i++) {
    const segmentationClass = requiredSegmentationClasses[i];

    const requiredPhasesForSegmentation: any = requiredPhases; // TODO, see above
    const phasesAreRequiredForSegmentation = !!requiredPhasesForSegmentation?.length;

    // case a) if phases are required, check if segmentation labels exist for each phase
    if (phaseLabelsAreRequiredForDicom && phasesAreRequiredForSegmentation) {
      for (let j = 0; j < requiredPhasesForSegmentation?.length; j++) {
        // first, get the phase label for the required phase
        const requiredPhaseValue = requiredPhasesForSegmentation[j];
        const phaseLabel = phaseLabels?.find(
          (phaseLabel: { dicom: any; phaseClass: string }) =>
            phaseLabel?.dicom === dicom?.uuid && phaseLabel?.phaseClass === requiredPhaseValue
        );

        // next, look for the segmentation label for the required phase
        const segmentationLabel = segmentationLabels?.find(
          (segmentationLabel: { frame: any; segmentationClass: any }) =>
            segmentationLabel?.frame === phaseLabel?.frame &&
            segmentationLabel?.segmentationClass === segmentationClass?.uuid
        );

        // if the segmentationLabel label does not exist, return false
        if (!segmentationLabel)
          return {
            segmentationsAreLabeled: false,
            segmentationsAreRequired: true,
          };

        // if the segmentation is not fully visible, skip it
        if (segmentationLabel?.isFullyVisible === false) continue;

        // if the segmentation paths are empty, return false
        if (!segmentationLabel?.paths?.length)
          return {
            segmentationsAreLabeled: false,
            segmentationsAreRequired: true,
          };
      }
    }

    // case b) if phases are not required, check if segmentation labels exist for the dicom
    else {
      const segmentationLabel = segmentationLabels?.find(
        (segmentationLabel: { dicom: any; segmentationClass: any }) =>
          segmentationLabel?.dicom === dicom?.uuid &&
          segmentationLabel?.segmentationClass === segmentationClass?.uuid
      );

      // if the segmentation label does not exist, return false
      if (!segmentationLabel)
        return {
          segmentationsAreLabeled: false,
          segmentationsAreRequired: true,
        };

      // if the segmentation is not fully visible, skip it
      if (segmentationLabel?.isFullyVisible === false) continue;

      // if the segmentation paths are empty, return false
      if (!segmentationLabel?.paths?.length)
        return {
          segmentationsAreLabeled: false,
          segmentationsAreRequired: true,
        };
    }
  }

  return {
    segmentationsAreLabeled: true,
    segmentationsAreRequired: true,
  };
};
